import { PAGE_TYPE_ENUM } from "@shared/types";
import { CompleteRegistration } from "components/MachineRegistration/CompleteRegistration";
import { CreateAccountForm } from "components/MachineRegistration/CreateAccountForm";
import { MachineBanner } from "components/MachineRegistration/MachineBanner";
import { MachineDetails } from "components/MachineRegistration/MachineDetails";
import { MachinesList } from "components/MachineRegistration/MachinesList";
import { SliderWithImageAndStrucutredText } from "components/common/SliderWithImageAndStructuredText.tsx/SliderWithImageAndStrucutredText";
import PageLayout from "components/layout/PageLayout";
import { setDYContext } from "config";
import { useLogContext } from "context/log";
import { useTranslationContext } from "context/translation";
import { UserContext } from "context/user";
import { FORM_ERROR, ValidationErrors } from "final-form";
import {
  generatePageId,
  logMachineRegistrationSuccessEvent,
} from "helpers/analyticsLogger";
import { setCachingHeader } from "helpers/cacheHeader";
import { formatDateToIso } from "helpers/date";
import { validateWithYup } from "helpers/validation";
import {
  RegisterMachineFormValues,
  useMachineRegistrationForm,
} from "hooks/machineRegistration/useMachineRegistrationForm";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import { FC, useContext, useEffect, useRef } from "react";
import { Form } from "react-final-form";
import { createDatoClient } from "services/datocms";
import { MachineRegistrationQuery, getSdk } from "services/datocms/generated";
import {
  CreateRegistrationRecordInput,
  useCreateAccountAndRegistrationRecordMutation,
  useCreateRegistrationRecordMutation,
} from "services/graphql/generated";
import { createQueryParamsForCaching } from "utils/create-query-params";
import st from "./index.module.scss";

interface RegisterYourProductPageProps {
  pageData: MachineRegistrationQuery["machineRegistrationPage"];
}

export const getServerSideProps: GetServerSideProps<
  RegisterYourProductPageProps
> = async (context) => {
  setCachingHeader(context);

  try {
    const { machineRegistrationPage } = await getSdk(
      createDatoClient(
        context.preview,
        createQueryParamsForCaching({ queryType: "MachineRegistration" }),
      ),
    ).MachineRegistration();

    return {
      props: {
        pageData: machineRegistrationPage,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const RegisterYourProduct: FC<RegisterYourProductPageProps> = ({
  pageData,
}) => {
  const router = useRouter();
  const { unknownRegistrationError } = useTranslationContext();

  const { useLogPageView } = useLogContext();

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.other,
    id: generatePageId("machine-registration"),
  });

  const { currentUserId } = useContext(UserContext);
  const [createRegistrationRecord] = useCreateRegistrationRecordMutation();
  const [createAccountAndRegistrationRecord] =
    useCreateAccountAndRegistrationRecordMutation();

  const { machineSchema } = useMachineRegistrationForm(currentUserId);

  const validationErrorsRef = useRef<ValidationErrors>({});

  const slug = router.query.slug?.[0] ?? "";

  const knownMachine = pageData?.machines.find(
    (machine) => machine.slug === slug,
  );

  useEffect(() => {
    setDYContext("OTHER", ["Register Your Product"]);
  }, []);

  const onSubmit = async (values: RegisterMachineFormValues) => {
    const registrationRercord: CreateRegistrationRecordInput = {
      purchaseLocation: values.purchaseLocation,
      gtin: values.itemId,
      emailOptIn: values.registrationEmailOptIn,
      purchaseDate: formatDateToIso(values.purchaseDate as Date),
    };

    if (pageData?.customData) {
      const customData: Record<string, string> = pageData.customData.reduce(
        (prev, current) => {
          if (current.key) {
            return { ...prev, [current.key]: current.value };
          }
          return prev;
        },
        {},
      );
      registrationRercord.customData = JSON.stringify(customData);
    }

    let emailAddress = values.email;
    let firstName = values.firstName;
    let lastName = values.lastName;

    try {
      if (currentUserId) {
        const { data } = await createRegistrationRecord({
          variables: {
            input: registrationRercord,
          },
        });
        emailAddress = data?.createRegistrationRecord?.user.email ?? "";
        firstName = data?.createRegistrationRecord?.user.firstName ?? "";
        lastName = data?.createRegistrationRecord?.user.lastName ?? "";
      } else {
        await createAccountAndRegistrationRecord({
          variables: {
            registrationRecordInput: registrationRercord,
            userInput: {
              email: values.email,
              password: values.password,
              firstName: values.firstName,
              lastName: values.lastName,
              emailOptIn: values.emailOptIn,
              dateOfBirth: values.birthday && formatDateToIso(values.birthday),
            },
          },
        });
      }
    } catch (err) {
      return {
        [FORM_ERROR]: unknownRegistrationError,
      };
    }
    const machine = pageData?.machines.find(
      (machine) => machine.gtin === values.itemId,
    );
    logMachineRegistrationSuccessEvent({
      sodastream_model: machine?.gtin as string,
      purchase_location: values.purchaseLocation,
      purchase_date: values.purchaseDate
        ? formatDateToIso(values.purchaseDate)
        : "",
      email_address: emailAddress,
      first_name: firstName,
      last_name: lastName,
      accept_marketing: values.registrationEmailOptIn?.toString() || "false",
    });

    // Set a session storage item before redirecting to show the registration success message in discover-machine page
    sessionStorage.setItem("registered", "true");
    await router.push(`/discover-machine/${machine?.slug}`);
  };

  return (
    <PageLayout seo={pageData?.seo} className="bg-whiteSmoke">
      <div className="container mx-auto flex flex-col gap-6 py-6 min-w-[325px]">
        <div className={st.headerWrapper}>
          <h1>{pageData?.headerText}</h1>
          {pageData?.subHeaderText && (
            <p>
              <strong>{pageData.subHeaderText}</strong>
            </p>
          )}
        </div>
        <Form<RegisterMachineFormValues>
          initialValues={{
            itemId: knownMachine ? (knownMachine.gtin as string) : "",
            registrationEmailOptIn: false,
            terms: false,
          }}
          validate={validateWithYup(machineSchema)}
          onSubmit={onSubmit}
          render={(formProps) => {
            validationErrorsRef.current = formProps.errors;
            return (
              <form
                className="flex flex-col gap-6"
                onSubmit={formProps.handleSubmit}
              >
                {knownMachine ? (
                  <MachineBanner
                    machine={knownMachine}
                    backgroundImage={
                      pageData?.machineBannerBackground?.responsiveImage
                    }
                  />
                ) : (
                  <MachinesList
                    header={pageData?.machinesHeader}
                    machines={pageData?.machines}
                  />
                )}
                <MachineDetails
                  header={pageData?.machineDetailsHeader}
                  locations={pageData?.purchaseLocation}
                />
                {!currentUserId && <CreateAccountForm />}
                <CompleteRegistration
                  emailLabel={pageData?.emailSubscriptionText}
                  termsLabel={pageData?.termsOfServiceText}
                  formProps={formProps}
                />
              </form>
            );
          }}
        />
        {pageData?.benefitsOfAccount && (
          <SliderWithImageAndStrucutredText
            sliderData={pageData.benefitsOfAccount}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default RegisterYourProduct;
